import { Link, Navigate, useParams } from 'react-router-dom';
import { CalendarToday, Description, Mic, PlayArrow, Psychology } from '@mui/icons-material';
import { Badge, Button, Card, CardContent, CardHeader } from '@mui/material';
import { useCallback, useState } from 'react';

import { AppHeader } from '../components/app-header';
import { CardTitle } from '../components/card-title';
import { DataTable } from '../components/data-table';
import { DebugCard } from '../components/debug-card';
import { ExplorableId } from '../components/explorable-id';
import { LoadingState } from '../components/loading-state';
import {
  useGetTranscriptDetailsQuery,
  adminV2Api,
  useGetProviderNoteProcessingSessionsQuery,
} from '../store/api';
import { formatDateTime, getStatusBadgeColor } from '../utils/formatting';
import APIService from '@services/APIService';
import { useAccessToken } from '@features/user';
import { useDispatch } from 'react-redux';

const INTAKE_TEMPLATE = 'p:21fd6721-0be9-48cf-9de7-b7f341d15940';

export default function TranscriptDetailPage() {
  const {
    providerId: urlProviderId,
    transcriptId: transcriptIdParam,
    id: legacyId,
  } = useParams<{ providerId?: string; transcriptId?: string; id?: string }>();
  const transcriptId = transcriptIdParam || legacyId || ''; // Keep as string since it's a UUID

  // Get URL for each row
  const getRowUrl = useCallback(
    (note: any) => {
      return urlProviderId
        ? `/admin-v2/provider/${urlProviderId}/notes/${note.note_id}`
        : `/admin-v2/note/${note.note_id}`;
    },
    [urlProviderId],
  );

  // State for note processing
  const [isProcessing, setIsProcessing] = useState(false);
  const { getAccessToken } = useAccessToken();
  const dispatch = useDispatch();

  // Call hook unconditionally (React rules of hooks)
  const {
    data,
    isLoading,
    error: _error,
    refetch,
  } = useGetTranscriptDetailsQuery(
    {
      transcriptionId: transcriptId,
    },
    { skip: !transcriptId },
  );

  // Prefer the provider from the URL; fall back to the transcript response
  const providerId = urlProviderId
    ? decodeURIComponent(urlProviderId)
    : data?.provider?.provider_id || data?.transcription?.provider_id || '';

  // Get related note processing sessions for this transcript
  const { data: npsData } = useGetProviderNoteProcessingSessionsQuery(
    {
      providerId: providerId || '',
      transcriptionSessionId: transcriptId,
      perPage: 10,
    },
    { skip: !providerId || !transcriptId },
  );

  if (!transcriptId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Transcript not found</h2>
            <p className="text-gray-600 mt-2">Invalid transcript ID.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingState message="Loading transcript details..." />;
  }

  // Show detailed error information
  if (_error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-900">Error Loading Transcript</h2>
            <p className="text-gray-600 mt-2">There was an error loading the transcript details.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (!data?.transcription) {
    console.error('No transcription data in response:', data);
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Transcript not found</h2>
            <p className="text-gray-600 mt-2">No transcript data was returned from the API.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // Legacy /admin-v2/transcript/:id URL — redirect to the provider-scoped URL
  if (!urlProviderId && providerId) {
    return (
      <Navigate
        to={`/admin-v2/provider/${encodeURIComponent(providerId)}/transcriptions/${transcriptId}`}
        replace
      />
    );
  }

  const { transcription, provider, notes, encounter, appointment } = data;

  // Type augmentation for provider to include templates and timezone
  interface ExtendedProvider {
    templates?: Array<{
      display_name: string;
      template_id: string;
      type: string;
    }>;
    default_timezone?: string;
  }

  const extendedProvider = provider as ExtendedProvider;

  // Helper function to format note title from transcript date
  const formatNoteTitle = (createdAt: string, userTimezone?: string) => {
    const date = new Date(createdAt);

    // Use user's timezone if available, otherwise fallback to EST
    const timezone = userTimezone || 'America/New_York';

    // Format date and time in user's timezone (MM-DD-YYYY HH:MM)
    const formattedDateTime = date.toLocaleString('en-US', {
      timeZone: timezone,
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    return `Recovered Note (${formattedDateTime})`;
  };

  // Handle starting note processing
  const handleStartNoteProcessing = async () => {
    if (!transcription || !provider || !providerId) return;

    setIsProcessing(true);
    try {
      const accessToken = await getAccessToken();

      // Use the first template from the user's template list, or fallback to default
      const templateId = extendedProvider.templates?.[0]?.template_id || INTAKE_TEMPLATE;
      console.log('Template ID being used:', templateId);
      console.log('Provider templates:', extendedProvider.templates);

      const noteTitle = formatNoteTitle(
        transcription.start_time || '',
        extendedProvider.default_timezone,
      );

      const response = await APIService.makeAPIPostRequest({
        requestString: '/noteProcessingSession/startNoteProcessingForUser',
        accessToken,
        body: {
          target_user_id: providerId,
          transcription_session_id: transcription.transcription_session_id,
          note_title: noteTitle,
          template_id: templateId,
        },
      });

      if (response.ok) {
        console.log('Note processing started successfully');

        // Store the current notes_created count to detect when it changes
        const currentNotesCreated = transcription.notes_created || 0;

        // Invalidate cache tags to refresh both the Notes Created count and the notes table
        dispatch(
          adminV2Api.util.invalidateTags([
            { type: 'Transcription', id: transcriptId },
            { type: 'Note', id: providerId },
          ]),
        );

        // Poll for data update with increasing intervals
        const pollForUpdate = (attempt = 1) => {
          const delay = Math.min(1000 * attempt, 5000); // 1s, 2s, 3s, 4s, 5s max

          setTimeout(async () => {
            try {
              // Trigger a fresh refetch
              const result = await refetch();
              const updatedData = result.data;

              if (updatedData?.transcription?.notes_created > currentNotesCreated) {
                // Data has been updated, re-enable the button
                console.log('Notes Created updated successfully');
                setIsProcessing(false);
              } else if (attempt < 10) {
                // Try again with longer delay
                console.log(`Polling attempt ${attempt}, no update yet`);
                pollForUpdate(attempt + 1);
              } else {
                // Give up after 10 attempts (~30 seconds total)
                console.log('Polling timeout, re-enabling button');
                setIsProcessing(false);
              }
            } catch (error) {
              console.error('Error during polling:', error);
              setIsProcessing(false);
            }
          }, delay);
        };

        // Start polling
        pollForUpdate();

        // Optionally show a success message or navigate somewhere
      } else {
        console.error('Failed to start note processing:', response);
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Failed to start note processing:', error);
      setIsProcessing(false);
    }
    // Note: setIsProcessing(false) is now handled in the setTimeout for successful cases
  };

  // Add safety checks for the destructured data
  if (!transcription) {
    console.error('No transcription in data:', data);
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Transcript data missing</h2>
            <p className="text-gray-600 mt-2">
              The transcript data is missing from the API response.
            </p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const notesColumns = [
    {
      key: 'note_id',
      label: 'Note ID',
      render: (value: string) => <span className="text-sm font-medium text-blue-600">{value}</span>,
    },
    {
      key: 'template',
      label: 'Template',
      render: (value: string) => <span className="text-sm text-gray-900">{value}</span>,
    },
    {
      key: 'encounter_type',
      label: 'Encounter Type',
      render: (value: string) => <span className="text-sm text-gray-700">{value || 'N/A'}</span>,
    },
    {
      key: 'patient_name',
      label: 'Patient',
      render: (value: string) => <span className="text-sm text-gray-700">{value || 'N/A'}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (value: string) => (
        <Badge color={getStatusBadgeColor(value)}>{value.replace('_', ' ')}</Badge>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value: string) => (
        <span className="text-sm text-gray-500">{formatDateTime(value)}</span>
      ),
    },
  ];

  return (
    <>
      {/* Transcript header strip; provider identity lives in the passport above */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Mic className="mr-2 h-6 w-6" />
              Transcript
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {formatDateTime(transcription.start_time || '')} · {transcription.chunk_count} chunks
              · {transcription.notes_created} notes created
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <ExplorableId
                label="transcript"
                value={transcription.transcription_session_id}
                resourceType="transcription_session"
                organizationId={transcription.organization_id || provider?.organization_id}
              />
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Badge color={getStatusBadgeColor(transcription.job_status)}>
              {transcription.job_status.replace('_', ' ')}
            </Badge>
            <Button
              variant="contained"
              color="primary"
              onClick={handleStartNoteProcessing}
              disabled={isProcessing}
              startIcon={<PlayArrow />}
            >
              {isProcessing ? 'Submitting...' : 'Generate Note'}
            </Button>
            {providerId && (
              <Link to={`/admin-v2/provider/${encodeURIComponent(providerId)}/transcriptions`}>
                <Button variant="outlined" size="small">
                  Back to Transcriptions
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Debug Card */}
      <DebugCard
        transcriptionId={transcription.transcription_session_id}
        noteProcessingId={npsData?.items?.[0]?.note_processing_session_id}
      />

      {/* Related Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 mt-6">
        {/* Encounter Information */}
        {encounter ? (
          <Card>
            <CardHeader
              title={
                <CardTitle className="flex items-center">
                  <Description className="mr-2 h-5 w-5" />
                  Encounter
                </CardTitle>
              }
            />
            <CardContent>
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-900">{encounter.patient_id}</div>
                <div className="text-sm text-gray-500">
                  Org: {encounter.organization_id || 'N/A'}
                </div>
                <div className="text-xs text-gray-400">{formatDateTime(encounter.created_at)}</div>
                <div className="mt-3">
                  <Link
                    to={
                      providerId
                        ? `/admin-v2/provider/${encodeURIComponent(providerId)}/encounters/${encounter.encounter_id}`
                        : `/admin-v2/encounter/${encounter.encounter_id}`
                    }
                  >
                    <Button variant="outlined" size="small" className="w-full">
                      View Encounter
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader
              title={
                <CardTitle className="flex items-center">
                  <Description className="mr-2 h-5 w-5" />
                  Encounter
                </CardTitle>
              }
            />
            <CardContent>
              <p className="text-sm text-gray-500">No associated encounter</p>
            </CardContent>
          </Card>
        )}

        {/* Appointment Information */}
        {appointment ? (
          <Card>
            <CardHeader
              title={
                <CardTitle className="flex items-center">
                  <CalendarToday className="mr-2 h-5 w-5" />
                  Appointment
                </CardTitle>
              }
            />
            <CardContent>
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-900">
                  {appointment.appointment_id}
                </div>
                <div className="text-sm text-gray-500">{appointment.title}</div>
                <div className="text-xs text-gray-400">
                  {formatDateTime(appointment.start_time)}
                </div>
                <Badge color={getStatusBadgeColor(appointment.status)}>{appointment.status}</Badge>
                <div className="mt-3">
                  <Link
                    to={
                      providerId
                        ? `/admin-v2/provider/${encodeURIComponent(providerId)}/appointments/${appointment.appointment_id}`
                        : `/admin-v2/appointment/${appointment.appointment_id}`
                    }
                  >
                    <Button variant="outlined" size="small" className="w-full">
                      View Appointment
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader
              title={
                <CardTitle className="flex items-center">
                  <CalendarToday className="mr-2 h-5 w-5" />
                  Appointment
                </CardTitle>
              }
            />
            <CardContent>
              <p className="text-sm text-gray-500">No associated appointment</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Note Processing Sessions Information */}
      {npsData && npsData.items && npsData.items.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Psychology className="mr-2 h-5 w-5" />
              Note Processing Sessions ({npsData.items.length})
            </h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {npsData.items.map((nps: any) => (
                <div key={nps.note_processing_session_id} className="border rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Session ID:</span>
                      <span className="text-sm text-gray-900">
                        {nps.note_processing_session_id
                          ? `${nps.note_processing_session_id.substring(0, 8)}...`
                          : 'No session ID'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Status:</span>
                      <Badge color={getStatusBadgeColor(nps.status)}>
                        {nps.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Encounter:</span>
                      <span className="text-sm text-gray-900">
                        {nps.encounter_id
                          ? `${nps.encounter_id.substring(0, 8)}...`
                          : 'No encounter'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Note:</span>
                      <span className="text-sm text-gray-900">
                        {nps.note_id ? `${nps.note_id.substring(0, 8)}...` : 'No note'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Created:</span>
                      <span className="text-sm text-gray-900">
                        {formatDateTime(nps.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Link
                      to={`/admin-v2/note-processing-session/${nps.note_processing_session_id}`}
                    >
                      <Button variant="outlined" size="small">
                        View Session Details
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Notes Created from Transcript */}
      <DataTable
        title="Notes Created from This Transcript"
        columns={notesColumns as any}
        data={notes || []}
        loading={!notes}
        getRowUrl={getRowUrl}
        emptyMessage="No notes were created from this transcript"
      />
    </>
  );
}
